import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  GetFinalExamByIdQuery,
  SelectedOption,
  UserQuestion,
} from '../__generated__/graphql';
import { useState, useCallback } from 'react';
import { GET_FINAL_EXAM_BY_ID } from '../graphql/getFinalExamById';
import { UPDATE_FINAL_EXAM } from '../graphql/updateFinalExam';
import { CREATE_FINAL_EXAM } from '../graphql/createFinalExam';
import { BOOKMARK_QUESTION } from '../graphql/bookMarkQuestion';
import { GET_BOOKMARKED_QUESTIONS_FOR_USER } from '../graphql/getBookmarkedQuestionsForUser';
import { GET_ALL_FINAL_EXAMS_FOR_USER } from '../graphql/getAllFinalExamsForUser';
import { GET_ACTIVE_FINAL_EXAM } from '../graphql/getActiveFinalExam';

export const useFinalExam = () => {
  const [currIndex, setCurrIndex] = useState<number>(0);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState<boolean>(false);
  
  const navigate = useNavigate();
  const client = useApolloClient();
  const { id } = useParams();
  const { state } = useLocation();

  // Queries
  const { data, loading } = useQuery<GetFinalExamByIdQuery>(GET_FINAL_EXAM_BY_ID, {
    variables: { id },
    skip: !id,
  });



  const { data: bookmarkedQuestionsData } = useQuery(
    GET_BOOKMARKED_QUESTIONS_FOR_USER,
  );

  // Mutations
  const [updateFinalExam] = useMutation(UPDATE_FINAL_EXAM);
  const [createFinalExam] = useMutation(CREATE_FINAL_EXAM);
  const [bookmarkQuestion] = useMutation(BOOKMARK_QUESTION);

  // Derived state
  const userQuestions = data?.getFinalExamById?.userQuestions;
  const finalExamData = data?.getFinalExamById;

  // Helper functions
  const isCurrentQuestionBookmarked = useCallback(() => {
    if (!bookmarkedQuestionsData?.getBookmarkedQuestionsForUser || !userQuestions) {
      return false;
    }
    const currentQuestionId = userQuestions[currIndex]?.question.id;
    return bookmarkedQuestionsData.getBookmarkedQuestionsForUser.some(
      (question: { id: string }) => question.id === currentQuestionId,
    );
  }, [bookmarkedQuestionsData, userQuestions, currIndex]);

  const isAllQuestionsCompleted = useCallback(() => {
    if (!userQuestions) {
      return false;
    }
    for (const userQuestion of userQuestions) {
      if (!userQuestion.selectedOption?.desc) {
        return false;
      }
    }
    return true;
  }, [userQuestions]);

  const calculateScore = useCallback(() => {
    let score = 0;
    for (const userQuestion of userQuestions ?? []) {
      if (userQuestion.selectedOption?.isAnswer) {
        score++;
      }
    }
    return score;
  }, [userQuestions]);

  // Event handlers
  const handleBookmarkToggle = useCallback(async () => {
    if (!userQuestions) return;

    const currentQuestionId = userQuestions[currIndex]?.question.id;
    if (!currentQuestionId) return;

    try {
      // Use the single bookmarkQuestion mutation for both bookmarking and unbookmarking
      await bookmarkQuestion({
        variables: { questionId: currentQuestionId },
        refetchQueries: [{ query: GET_BOOKMARKED_QUESTIONS_FOR_USER }],
      });
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  }, [userQuestions, currIndex, bookmarkQuestion]);

  const handleUpdateFinalExam = useCallback(async (
    questionId: string,
    selectedOption: SelectedOption,
  ) => {
    if (!userQuestions || !id) return;

    const updateFinalExamInput = {
      id,
      questionToBeUpdated: {
        questionId,
        selectedOption: {
          desc: selectedOption?.desc || '',
          isAnswer: selectedOption?.isAnswer || false,
        },
      },
      isCompleted: false,
    };

    try {
      await updateFinalExam({
        variables: { updateFinalExamInput },
        update: (cache, { data: updateData }) => {
          if (updateData?.updateFinalExam) {
            // The mutation returns a UserQuestion object with questionId, question, and selectedOption
            const updatedUserQuestion = updateData.updateFinalExam;
            
            // Update the specific question in the cache
            const copyQuestions = userQuestions.map((userQuestion) =>
              userQuestion.question.id === questionId
                ? { ...userQuestion, selectedOption: updatedUserQuestion.selectedOption }
                : userQuestion,
            );
            
            cache.writeQuery({
              query: GET_FINAL_EXAM_BY_ID,
              variables: { id },
              data: {
                getFinalExamById: {
                  ...data?.getFinalExamById,
                  userQuestions: copyQuestions,
                },
              },
            });
          }
        },
      });
    } catch (error) {
      console.error('Error updating final exam: ', error);
    }
  }, [userQuestions, id, updateFinalExam, data?.getFinalExamById]);

  const handleCompleteFinalExam = useCallback(async () => {
    if (!userQuestions || !id) return;

    // For completion, we need to update the last question and mark as completed
    const lastQuestion = userQuestions[userQuestions.length - 1];
    const updateFinalExamInput = {
      id,
      questionToBeUpdated: {
        questionId: lastQuestion.questionId,
        selectedOption: {
          desc: lastQuestion.selectedOption?.desc || '',
          isAnswer: lastQuestion.selectedOption?.isAnswer || false,
        },
      },
      isCompleted: true,
    };

    try {
      await updateFinalExam({
        variables: { updateFinalExamInput },
        update: (cache, { data: updateData }) => {
          if (updateData?.updateFinalExam) {
            // The mutation returns a UserQuestion object
            const updatedUserQuestion = updateData.updateFinalExam;
            
            // Update the final exam cache to mark as completed
            cache.writeQuery({
              query: GET_FINAL_EXAM_BY_ID,
              variables: { id },
              data: {
                getFinalExamById: {
                  ...data?.getFinalExamById,
                  isCompleted: true,
                  completedAt: new Date().toISOString(),
                  // Update the last question's selectedOption
                  userQuestions: userQuestions.map((userQuestion, index) =>
                    index === userQuestions.length - 1
                      ? { ...userQuestion, selectedOption: updatedUserQuestion.selectedOption }
                      : userQuestion
                  ),
                },
              },
            });

            // Update the my-exams cache to include this completed exam
            try {
              const existingExams = cache.readQuery({
                query: GET_ALL_FINAL_EXAMS_FOR_USER,
              }) as any;

              if (existingExams?.getAllFinalExamsForUser) {
                const updatedExam = {
                  ...data?.getFinalExamById,
                  isCompleted: true,
                  completedAt: new Date().toISOString(),
                  // Update the last question's selectedOption
                  userQuestions: userQuestions.map((userQuestion, index) =>
                    index === userQuestions.length - 1
                      ? { ...userQuestion, selectedOption: updatedUserQuestion.selectedOption }
                      : userQuestion
                  ),
                };

                // Check if exam already exists in the list
                const examExists = existingExams.getAllFinalExamsForUser.some(
                  (exam: any) => exam.id === id
                );

                if (!examExists) {
                  // Add the completed exam to the my-exams list
                  cache.writeQuery({
                    query: GET_ALL_FINAL_EXAMS_FOR_USER,
                    data: {
                      getAllFinalExamsForUser: [
                        updatedExam,
                        ...existingExams.getAllFinalExamsForUser,
                      ],
                    },
                  });
                } else {
                  // Update the existing exam in the list
                  const updatedExams = existingExams.getAllFinalExamsForUser.map(
                    (exam: any) => exam.id === id ? updatedExam : exam
                  );
                  cache.writeQuery({
                    query: GET_ALL_FINAL_EXAMS_FOR_USER,
                    data: {
                      getAllFinalExamsForUser: updatedExams,
                    },
                  });
                }
              }
            } catch (error) {
              // If the cache doesn't exist yet, that's fine - it will be populated when the user visits my-exams
              console.log('My-exams cache not yet initialized');
            }

            // Update the active final exam cache to remove this exam since it's now completed
            try {
              cache.writeQuery({
                query: GET_ACTIVE_FINAL_EXAM,
                data: {
                  getActiveFinalExam: null,
                },
              });
            } catch (error) {
              // If the cache doesn't exist yet, that's fine
              console.log('Active final exam cache not yet initialized');
            }
          }
        },
      });
      setIsCompleteModalOpen(true);
    } catch (error) {
      console.error('Error completing final exam: ', error);
    }
  }, [userQuestions, id, updateFinalExam, data?.getFinalExamById]);

  const handleCreateFinalExam = useCallback(async (
    numberOfQuestions: number,
    testedTags: string[],
  ) => {
    try {
      const { data: newFinalExamData } = await createFinalExam({
        variables: {
          createFinalExamInput: {
            numberOfQuestions,
            testedTags,
          },
        },
      });

      if (newFinalExamData?.createFinalExam) {
        // Update the active final exam cache to include this new exam
        try {
          client.writeQuery({
            query: GET_ACTIVE_FINAL_EXAM,
            data: {
              getActiveFinalExam: newFinalExamData.createFinalExam,
            },
          });
        } catch (error) {
          // If the cache doesn't exist yet, that's fine
          console.log('Active final exam cache not yet initialized');
        }

        // Navigate to the new final exam
        navigate(`/final-exam/${newFinalExamData.createFinalExam.id}`, { 
          state: { 
            email: state?.email 
          } 
        });
      }
    } catch (error) {
      console.error('Error creating final exam: ', error);
      // Re-throw the error so it can be caught by the calling component
      throw error;
    }
  }, [createFinalExam, navigate, state?.email, client]);

  const handleNavigateToDashboard = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);

  const handleNavigateToHistory = useCallback(() => {
    navigate('/my-exams');
  }, [navigate]);

  const handleCloseCompleteModal = useCallback(() => {
    setIsCompleteModalOpen(false);
  }, []);

  const handleQuestionSelection = useCallback((questionId: string, selectedOption: SelectedOption) => {
    if (!finalExamData?.isCompleted) {
      handleUpdateFinalExam(questionId, selectedOption);
    }
  }, [finalExamData?.isCompleted, handleUpdateFinalExam]);

  const handlePreviousQuestion = useCallback(() => {
    setCurrIndex((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);

  const handleNextQuestion = useCallback(() => {
    if (userQuestions) {
      // If we're on the last question and all questions are answered, complete the final exam
      if (currIndex === userQuestions.length - 1 && isAllQuestionsCompleted()) {
        handleCompleteFinalExam();
        return;
      }
      
      // Otherwise, move to next question
      setCurrIndex((prev) =>
        prev < userQuestions.length - 1 ? prev + 1 : prev,
      );
    }
  }, [userQuestions, currIndex, isAllQuestionsCompleted, handleCompleteFinalExam]);

  const isCurrentQuestionAnswered = useCallback(() => {
    if (!userQuestions || !userQuestions[currIndex]) return false;
    return !!userQuestions[currIndex].selectedOption?.desc;
  }, [userQuestions, currIndex]);

  return {
    // State
    currIndex,
    isCompleteModalOpen,
    loading,
    
    // Data
    userQuestions,
    finalExamData,
    bookmarkedQuestionsData,
    
    // Computed values
    isCurrentQuestionBookmarked: isCurrentQuestionBookmarked(),
    isAllQuestionsCompleted: isAllQuestionsCompleted(),
    isCurrentQuestionAnswered: isCurrentQuestionAnswered(),
    score: calculateScore(),
    totalQuestions: userQuestions?.length || 0,
    isCompleted: finalExamData?.isCompleted || false,
    
    // Event handlers
    handleBookmarkToggle,
    handleNavigateToDashboard,
    handleNavigateToHistory,
    handleCloseCompleteModal,
    handleCompleteFinalExam,
    handleCreateFinalExam,
    handleQuestionSelection,
    handlePreviousQuestion,
    handleNextQuestion,
  };
};
